import { useMutation } from '@apollo/client';
import React, { useEffect, useState } from 'react';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Tooltip from '@material-ui/core/Tooltip';
import ToggleInput from '@/se/components/inputs/ToogleInput';
import { Paragraph } from '@/se/components/typography';
import { useToaster } from '@/components/core/Toaster';
import { setSignalLogArchiving } from '../../../graph/hospitals';
import { formatDistanceToNow, parseISO, addDays, isBefore, format } from 'date-fns';

interface Props {
  data?: {
    id: string;
    signalLogArchivingEnabled?: boolean;
    signalLogArchivingEnabledAt?: string;
  };
}

const EXPIRY_DAYS = 2;

/**
 * Toggle for signal log archiving - SuperAdmin only feature for debugging.
 *
 * Features:
 * - Shows current enabled/disabled state
 * - Shows countdown to auto-expiry when enabled
 * - Warns when archiving is about to expire
 */
const SignalLogArchivingToggle = ({ data: props }: Props) => {
  const [setEnabled, { loading, data }] = useMutation(setSignalLogArchiving);
  const [value, setValue] = useState<boolean>(!!props?.signalLogArchivingEnabled);
  const toaster = useToaster();

  const isEnabled = !!props?.signalLogArchivingEnabled;
  const enabledAt = props?.signalLogArchivingEnabledAt;

  // Calculate expiry info with local timezone display
  const getExpiryInfo = () => {
    if (!isEnabled || !enabledAt) return null;

    try {
      const enabledDate = parseISO(enabledAt);
      const expiryDate = addDays(enabledDate, EXPIRY_DAYS);
      const isExpired = isBefore(expiryDate, new Date());

      // Format expiry date in user's local timezone
      const formattedExpiry = format(expiryDate, 'MMM d, h:mm a');

      if (isExpired) {
        return {
          text: 'Expired',
          tooltip: `Expired on ${formattedExpiry}`,
          color: 'error.main' as const,
        };
      }

      const timeLeft = formatDistanceToNow(expiryDate, { addSuffix: false });
      return {
        text: `Expires in ${timeLeft}`,
        tooltip: `Expires ${formattedExpiry}`,
        color: 'warning.main' as const,
      };
    } catch {
      return null;
    }
  };

  const expiryInfo = getExpiryInfo();

  useEffect(() => setValue(isEnabled), [isEnabled]);

  useEffect(() => {
    if (data?.setSignalLogArchiving) {
      setValue(!!data.setSignalLogArchiving.signalLogArchivingEnabled);
    }
  }, [data]);

  const handleChange = (enabled: boolean) => {
    setValue(enabled);

    setEnabled({
      variables: {
        id: props?.id,
        enabled,
      },
    }).catch(() => {
      setValue(!enabled);
      toaster.error('Unable to update signal log archiving. Please try again.');
    });
  };

  return (
    <Box display="flex" flexDirection="column">
      <Box display="flex" alignItems="center">
        <Box mr={0.5}>
          {/* @ts-ignore */}
          <ToggleInput isDisabled={loading} value={value} onChange={handleChange} style={{ margin: 0 }} />
        </Box>
        <Box flexGrow={1}>
          <Tooltip title="Enables signal logging to S3 for debugging patient journeys. Auto-expires after 2 days.">
            <Paragraph style={{ margin: 0, whiteSpace: 'pre-wrap', cursor: 'help' }}>Signal Log Archiving</Paragraph>
          </Tooltip>
        </Box>
      </Box>
      {expiryInfo && (
        <Box ml={4} mt={0.5}>
          <Tooltip title={expiryInfo.tooltip}>
            <Typography
              variant="caption"
              style={{ color: expiryInfo.color === 'error.main' ? '#f44336' : '#ff9800', cursor: 'help' }}
            >
              {expiryInfo.text}
            </Typography>
          </Tooltip>
        </Box>
      )}
    </Box>
  );
};

export default SignalLogArchivingToggle;
